import * as React from "react";
import { Form, Formik } from "formik";
import OnChangeComponent from "../utility/OnChangeComponent";
import { moneyFormatter } from "../../utils";
import TextField, { TextFieldInput } from "../core/TextField";
import NumberFormat from "react-number-format";
import {
  calculateCommission,
  extractInputsFromMonth,
  newCommissionMonthInputs,
} from "../../libs/commission_calculator";
import Button from "../core/Button";

interface IProps {
  onChange?: (values: any) => void;
  config?: ICommissionConfig;
  month?: ICommissionMonth;
  readOnly?: boolean;
  allowReset?: boolean;
}

const cformat = moneyFormatter.format;

function CommmissionCalculatorForm({
  onChange,
  config,
  month,
  readOnly = false,
  allowReset = false,
}: IProps) {
  return (
    <Formik
      initialValues={
        month ? extractInputsFromMonth(month) : newCommissionMonthInputs
      }
      onSubmit={() => {}}
      render={({ values, handleChange, setFieldValue, handleReset }) => {
        const configInUse = config || month.commissionConfig;

        const commissionValues = calculateCommission(values, configInUse);

        const quotaEnabled = quotaName =>
          !configInUse.disabledQuotas ||
          !configInUse.disabledQuotas.includes(quotaName);

        return (
          <Form>
            <OnChangeComponent
              onChange={onChange}
              value={{ values, result: commissionValues }}
              debounce={2000}
            />
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Monthly Amount</th>
                  <th>% of Quota</th>
                  <th>% Paid</th>
                  <th>Commission</th>
                </tr>
              </thead>
              <tbody>
                {quotaEnabled("annuities") && (
                  <tr>
                    <td>Monthly Annuities</td>
                    <td>
                      <QuotaField
                        value={values.annuities}
                        field={"annuities"}
                        set={setFieldValue}
                        readOnly={readOnly}
                      />
                    </td>
                    <td>
                      <TextField disabled={true} value={"N/A"} />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.annuities.percentPaid}
                        percent={true}
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.annuities.commission}
                        percent={false}
                      />
                    </td>
                  </tr>
                )}
                {quotaEnabled("agreements") && (
                  <tr>
                    <td>New Agreements / Adds</td>
                    <td>
                      <QuotaField
                        value={values.agreements}
                        field={"agreements"}
                        set={setFieldValue}
                        readOnly={readOnly}
                        status={
                          commissionValues.agreements.percentQuota >= 100
                            ? "GOOD"
                            : "BAD"
                        }
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.agreements.percentQuota}
                        percent={true}
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.agreements.percentPaid}
                        percent={true}
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.agreements.commission}
                        percent={false}
                      />
                    </td>
                  </tr>
                )}
                {quotaEnabled("labor") && (
                  <tr>
                    <td>Labor Profit</td>
                    <td>
                      <QuotaField
                        value={values.labor}
                        field={"labor"}
                        set={setFieldValue}
                        readOnly={readOnly}
                        status={
                          commissionValues.labor.percentQuota >= 100
                            ? "GOOD"
                            : "BAD"
                        }
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.labor.percentQuota}
                        percent={true}
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.labor.percentPaid}
                        percent={true}
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.labor.commission}
                        percent={false}
                      />
                    </td>
                  </tr>
                )}
                {quotaEnabled("hardwareSoftware") && (
                  <tr>
                    <td>Hardware / Software Profit</td>
                    <td>
                      <QuotaField
                        value={values.hardwareSoftware}
                        field={"hardwareSoftware"}
                        set={setFieldValue}
                        readOnly={readOnly}
                        status={
                          commissionValues.hardwareSoftware.percentQuota >= 100
                            ? "GOOD"
                            : "BAD"
                        }
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.hardwareSoftware.percentQuota}
                        percent={true}
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.hardwareSoftware.percentPaid}
                        percent={true}
                      />
                    </td>
                    <td>
                      <DisplayField
                        value={commissionValues.hardwareSoftware.commission}
                        percent={false}
                      />
                    </td>
                  </tr>
                )}
                <tr>
                  <td />
                  <td />
                  <td />
                  <td>Quota Average</td>
                  <td>
                    <DisplayField
                      value={commissionValues.quotaAverage}
                      percent={true}
                    />
                  </td>
                </tr>
                <tr>
                  <td />
                  <td />
                  <td />
                  <td>Total</td>
                  <td>
                    <DisplayField
                      value={commissionValues.total}
                      percent={false}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            {allowReset && (
              <div className="level">
                <div className="level-right">
                  <div className="level-item">
                    <Button onClick={handleReset}>
                      <i className="fa fa-undo" /> Reset
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    />
  );
}

export default CommmissionCalculatorForm;

function QuotaField({
  value,
  set,
  field,
  readOnly = false,
  status = "NEUTRAL",
}) {
  let statusClass;
  switch (status) {
    case "GOOD":
      statusClass = "bg-success";
      break;
    case "BAD":
      statusClass = "bg-warning";
      break;
    default:
      statusClass = "";
  }

  return (
    <NumberFormat
      value={value}
      onValueChange={values => set(field, values.value)}
      prefix={"$"}
      thousandSeparator={true}
      decimalScale={2}
      allowEmptyFormatting={true}
      isNumericString={true}
      customInput={TextFieldInput}
      readOnly={readOnly}
      disabled={readOnly}
      className={statusClass}
    />
  );
}

function DisplayField({ percent = false, value }) {
  return (
    <NumberFormat
      value={value}
      disabled={true}
      prefix={percent ? undefined : "$"}
      suffix={percent ? "%" : undefined}
      thousandSeparator={true}
      decimalScale={2}
      isNumericString={true}
      customInput={TextFieldInput}
    />
  );
}



// WEBPACK FOOTER //
// ./app/javascript/components/commission_calculator/CommissionCalculatorForm.tsx